// Topic deep page — the full file on a topic.
//
// The slide-over inspector is for quick peek; this is the "I want to read the
// whole thing, share it, draft a memo from it" surface. It owns its own
// scrolled column inside the surface canvas.
//
// Sections, top to bottom:
//   1. Hero: name, taxonomy crumbs, big score chips (readiness, relevance,
//      transformation level, timing).
//   2. Score breakdown: every readiness input with its 1/2/3, justification,
//      and source attribution. Same shape for relevance, broken down by the
//      three inside lenses (Client / Field / Enterprise) — each lens row
//      expands to show the underlying business-line scores that rolled up.
//   3. Outside → Inside impact: textual summary per lens.
//   4. Backcast H1 → H2 → H3 (Improve / Add / Transform). Pulled from the
//      topic's tipping_point + milestone_signal for now; later will source
//      from a dedicated `topic_backcast` table.
//   5. Search terms + share link.

import {
  LENSES,
  LENS_LABEL,
  lensOfBusinessLine,
  type Lens,
} from "../lib/macros.ts";
import type {
  BusinessLine,
  Metric,
  TopicWithContext,
} from "../lib/types.ts";

const escHtml = (s: string | null | undefined) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

interface Cb {
  onClose?: () => void;
}

export function renderTopicPage(
  host: HTMLElement,
  ctx: TopicWithContext,
  metrics: Metric[],
  businessLines: BusinessLine[],
  cb: Cb = {},
): () => void {
  const { topic } = ctx;

  // -------------------- Hero --------------------
  const taxonomy = ctx.memberships
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
    .map((m) => `${escHtml(m.category.name)} <span class="topic-crumb-arrow">›</span> ${escHtml(m.trend.name)}${m.is_primary ? "" : ' <span class="muted">(sec)</span>'}`)
    .join("&nbsp;&nbsp;·&nbsp;&nbsp;");

  const readinessVal = ctx.readiness?.avg_readiness_v1;
  const readinessChip = readinessVal != null
    ? `<span class="topic-score-value">${readinessVal.toFixed(2)}</span><span class="topic-score-scale">/3</span>`
    : `<span class="topic-score-value muted">—</span>`;

  // Per-lens relevance rollup
  const lensScores: Record<Lens, { values: number[]; bls: Array<{ bl: BusinessLine; score: number | null }> }> = {
    client: { values: [], bls: [] },
    field: { values: [], bls: [] },
    enterprise: { values: [], bls: [] },
  };
  for (const bl of businessLines) {
    const lens = lensOfBusinessLine(bl);
    const r = ctx.relevance[bl.key];
    const score = r?.score ?? null;
    if (score != null) lensScores[lens].values.push(score);
    lensScores[lens].bls.push({ bl, score });
  }
  const lensRollup: Record<Lens, number | null> = {
    client: lensScores.client.values.length ? lensScores.client.values.reduce((a, b) => a + b, 0) / lensScores.client.values.length : null,
    field: lensScores.field.values.length ? lensScores.field.values.reduce((a, b) => a + b, 0) / lensScores.field.values.length : null,
    enterprise: lensScores.enterprise.values.length ? lensScores.enterprise.values.reduce((a, b) => a + b, 0) / lensScores.enterprise.values.length : null,
  };
  const overallRelevance = (() => {
    const xs = LENSES.map((l) => lensRollup[l]).filter((v): v is number => v != null);
    if (!xs.length) return null;
    return xs.reduce((a, b) => a + b, 0) / xs.length;
  })();
  const relevanceChip = overallRelevance != null
    ? `<span class="topic-score-value">${overallRelevance.toFixed(2)}</span><span class="topic-score-scale">/3</span>`
    : `<span class="topic-score-value muted">—</span>`;

  // Transformation level — placeholder until schema exposes the real column.
  // Heuristic: h3_thesis_flag => Transform; otherwise Improve.
  const transformLevel = topic.h3_thesis_flag ? "Transform" : "Improve";
  const transformBadge = topic.h3_thesis_flag ? "transform" : "improve";

  // -------------------- Readiness breakdown --------------------
  const v1 = metrics.filter((m) => m.metric_set === "v1").sort((a, b) => a.sort_order - b.sort_order);
  const readinessRows = v1
    .map((m) => {
      const s = ctx.scores[m.key];
      const chip = s?.score
        ? `<span class="score-pill score-${s.score}${s.estimated ? " est" : ""}">${s.score}</span>`
        : `<span class="score-pill score-empty">—</span>`;
      const just = s?.justification ?? "";
      const sourceTag = s?.estimated ? '<span class="src-tag est-tag">AI estimate</span>' : '<span class="src-tag wired-tag">wired</span>';
      return `
        <div class="topic-input-row">
          <div class="topic-input-head">
            <span class="topic-input-name">${escHtml(m.name)}</span>
            ${chip}
          </div>
          <div class="topic-input-meta">${sourceTag}${m.threshold_low ? `<span class="muted">${escHtml((m.threshold_low.split("\n")[0] ?? "").trim())}</span>` : ""}</div>
          ${just ? `<div class="topic-input-just">${escHtml(just)}</div>` : ""}
        </div>
      `;
    })
    .join("");

  // -------------------- Relevance breakdown --------------------
  const lensSection = (lens: Lens) => {
    const data = lensScores[lens];
    const rollup = lensRollup[lens];
    const rollupLabel = rollup != null ? rollup.toFixed(2) : "—";
    const blRows = data.bls
      .map(({ bl, score }) => {
        const chip = score
          ? `<span class="score-pill score-${score}">${score}</span>`
          : `<span class="score-pill score-empty">—</span>`;
        return `
          <div class="topic-bl-row">
            <span class="topic-bl-name">${escHtml(bl.name)}</span>
            ${chip}
          </div>
        `;
      })
      .join("");
    return `
      <div class="topic-lens">
        <div class="topic-lens-head">
          <h4 class="topic-lens-name lens-${lens}">${LENS_LABEL[lens]}</h4>
          <span class="topic-lens-rollup">${rollupLabel}<span class="topic-score-scale">/3</span></span>
        </div>
        <div class="topic-lens-bls">${blRows}</div>
      </div>
    `;
  };

  // -------------------- Backcast H1 → H2 → H3 --------------------
  // For each phase we use what's known on the topic plus a templated framing.
  // Once `topic_backcast` table exists, this becomes data-driven.
  const phaseCard = (level: "H1" | "H2" | "H3", label: string, body: string) => `
    <div class="topic-phase topic-phase-${level.toLowerCase()}">
      <div class="topic-phase-head">
        <span class="topic-phase-tag">${level}</span>
        <span class="topic-phase-label">${label}</span>
      </div>
      <div class="topic-phase-body">${body}</div>
    </div>
  `;

  const backcastHTML = `
    ${phaseCard("H1", "Improve · 0–2y",
      escHtml(topic.recommendation ?? "")
        || '<span class="muted">No near-term recommendation captured. Add via Capture or curate in Inbox.</span>',
    )}
    ${phaseCard("H2", "Add · 2–5y",
      escHtml(topic.tipping_point ?? "")
        || '<span class="muted">No tipping-point thesis captured.</span>',
    )}
    ${phaseCard("H3", "Transform · 5–10y",
      escHtml(topic.milestone_signal ?? "")
        || '<span class="muted">No transformative milestone captured.</span>',
    )}
  `;

  // -------------------- Render --------------------
  host.innerHTML = `
    <article class="topic-page">
      <header class="topic-hero">
        <button class="topic-back" id="topicBack" title="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M15 6l-6 6 6 6"/></svg>
          <span>Back</span>
        </button>
        <div class="topic-crumbs">${taxonomy || '<span class="muted">No taxonomy</span>'}</div>
        <h1 class="topic-name">${escHtml(topic.name)}</h1>
        ${topic.definition ? `<p class="topic-definition">${escHtml(topic.definition)}</p>` : ""}
        <div class="topic-tag-row">
          <span class="topic-tag transform-${transformBadge}">${transformLevel}</span>
          ${topic.h3_thesis_flag ? '<span class="topic-tag h3">H3 thesis</span>' : ""}
          ${topic.ai_drafted ? '<span class="topic-tag ai">AI-drafted</span>' : '<span class="topic-tag curated">curated</span>'}
        </div>
        <div class="topic-score-row">
          <div class="topic-score">
            <span class="topic-score-label">Readiness</span>
            <span class="topic-score-chip">${readinessChip}</span>
            <span class="topic-score-meta">${ctx.readiness?.v1_metric_count ?? 0} of ${v1.length} inputs</span>
          </div>
          <div class="topic-score">
            <span class="topic-score-label">Relevance</span>
            <span class="topic-score-chip">${relevanceChip}</span>
            <span class="topic-score-meta">across ${LENSES.length} lenses</span>
          </div>
          <div class="topic-score">
            <span class="topic-score-label">Transformation</span>
            <span class="topic-score-chip"><span class="topic-score-value">${transformLevel}</span></span>
            <span class="topic-score-meta">${topic.h3_thesis_flag ? "long-horizon" : "near-term"}</span>
          </div>
          <div class="topic-score">
            <span class="topic-score-label">Timing</span>
            <span class="topic-score-chip"><span class="topic-score-value">${topic.h3_thesis_flag ? "5–10y" : "0–5y"}</span></span>
            <span class="topic-score-meta">est window</span>
          </div>
        </div>
      </header>

      <section class="topic-section">
        <h3 class="topic-section-head">Readiness · score breakdown</h3>
        <p class="topic-section-help">Each input scored 1/2/3 with its source. Average across inputs is the headline readiness.</p>
        <div class="topic-input-grid">${readinessRows}</div>
      </section>

      <section class="topic-section">
        <h3 class="topic-section-head">Relevance · outside → inside</h3>
        <p class="topic-section-help">How this topic lands across Client, Field, and Enterprise lenses. Each lens rolls up its constituent business lines.</p>
        <div class="topic-lens-grid">
          ${lensSection("client")}
          ${lensSection("field")}
          ${lensSection("enterprise")}
        </div>
      </section>

      <section class="topic-section">
        <h3 class="topic-section-head">Backcast · transformation phasing</h3>
        <p class="topic-section-help">How this topic is expected to move from incremental improvement → additive capability → transformative shift.</p>
        <div class="topic-phase-row">${backcastHTML}</div>
      </section>

      ${topic.search_terms?.length ? `
        <section class="topic-section">
          <h3 class="topic-section-head">Search terms</h3>
          <p class="topic-section-help">Used by source adapters to find evidence.</p>
          <div class="topic-chips">${topic.search_terms.map((t) => `<span class="topic-chip">${escHtml(t)}</span>`).join("")}</div>
        </section>` : ""}

      <footer class="topic-footer">
        <button class="btn" id="topicCopyLink">Copy share link</button>
        <a class="btn" href="#/cluster">Open in map →</a>
      </footer>
    </article>
  `;

  // Handlers
  host.querySelector<HTMLButtonElement>("#topicBack")?.addEventListener("click", () => {
    if (cb.onClose) cb.onClose();
    else history.back();
  });
  const copy = host.querySelector<HTMLButtonElement>("#topicCopyLink");
  copy?.addEventListener("click", async () => {
    const url = `${location.origin}/#/topic/${encodeURIComponent(topic.slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      copy.textContent = "Copied ✓";
      setTimeout(() => { copy.textContent = "Copy share link"; }, 1500);
    } catch {
      prompt("Copy this URL:", url);
    }
  });

  return () => { host.innerHTML = ""; };
}
